/**
 * View the app as another role (routes/view_as.py): a CM or system admin
 * borrows a short-lived, read-only token carrying one role, and gets their
 * own back on Exit.
 *
 * THE SERVER IS THE CONTROL. Every write sent with a view-as token is refused
 * (403 view_as_read_only) whatever this file does; what this file adds is
 * that nothing done while viewing is kept on the device to be sent LATER, as
 * the admin, after Exit: Field Mode's batches and the desktop sync queue
 * refuse to take anything (refuseWhileViewing), and the outbox waits.
 *
 * Both tokens live in localStorage: the view-as one where every request reads
 * a token (`mb_token`), the admin's beside it (`mb_token_admin`). Signing in
 * or out clears both, so a shared device never keeps an admin token behind a
 * session that looks ended. The workspace chosen as the admin is set aside
 * too, so the viewed role lands where that role would.
 */
import { BUILD_ID } from '../buildInfo';
import { apiUrl } from './serverConfig';

export const TOKEN_KEY = 'mb_token';
export const ADMIN_TOKEN_KEY = 'mb_token_admin';
const WORKSPACE_KEY = 'mb_workspace';
const ADMIN_WORKSPACE_KEY = 'mb_workspace_admin';

/** Fired (on window) when a write was refused because of a view-as token. */
export const READ_ONLY_EVENT = 'pitriot:view-as-read-only';

export function parseToken(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

const read = (storage, key) => {
  try { return storage?.getItem(key) ?? null; } catch { return null; }
};
const write = (storage, key, value) => {
  try {
    if (value == null) storage?.removeItem(key);
    else storage?.setItem(key, value);
  } catch { /* a refused write leaves the old value; nothing better to do */ }
};

/** What a token's claims say about viewing as: {role, sessionId, realRoles,
 *  exp}, or null for an ordinary token. */
export function viewAsOf(claims) {
  if (!claims?.vas) return null;
  return {
    role: (claims.roles || [])[0] || null,
    sessionId: String(claims.vas),
    realRoles: Array.isArray(claims.vab) ? claims.vab : [],
    exp: Number(claims.exp) || 0,
  };
}

/** The view this device is in now, or null. */
export function viewingAs(storage = globalThis.localStorage) {
  const token = read(storage, TOKEN_KEY);
  return token ? viewAsOf(parseToken(token)) : null;
}

export const isViewAsToken = token => !!(token && parseToken(token)?.vas);

/** Roles that may only read (lambda/permissions.py READ_ONLY_ROLES): an
 *  executive's own token (0098), refused every write by the server
 *  (403 read_only_account, routes/read_only.py). */
export const READ_ONLY_ROLES = ['company_executive'];

/** Whether these claims are an executive's own token, not a view as a role. */
export const isExecutive = claims => !!claims && !claims.vas && (claims.roles || []).length > 0
  && claims.roles.every(r => READ_ONLY_ROLES.includes(r));

/** This device's session when it may only read: a view as a role
 *  ({role, sessionId, ...}) or an executive's own ({role, executive: true});
 *  else null. Unlike viewingAs, an executive has no admin token to go back to. */
export function readOnlyNow(storage = globalThis.localStorage) {
  const token = read(storage, TOKEN_KEY);
  const claims = token ? parseToken(token) : null;
  if (!claims) return null;
  return viewAsOf(claims) || (isExecutive(claims) ? { role: claims.roles[0], executive: true } : null);
}

export class ViewAsReadOnly extends Error {
  constructor(role, code = 'view_as_read_only') {
    super('Read-only session: nothing is kept');
    this.code = code;
    this.role = role;
  }
}

const READ_ONLY_CODES = ['view_as_read_only', 'read_only_account'];

/** Whether an error is a refusal because this session may only read: the
 *  device's (ViewAsReadOnly) or the server's (403 view_as_read_only, or
 *  read_only_account for an executive). */
export const isReadOnlyRefusal = e => READ_ONLY_CODES.includes(e?.code)
  || READ_ONLY_CODES.includes(e?.response?.data?.code);

/** The error for a read-only session's refused write on this device. */
export const readOnlyError = view => new ViewAsReadOnly(view.role, view.executive ? 'read_only_account' : undefined);

export function announceReadOnly(role) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: { role: role || null } }));
}

/** Throws ViewAsReadOnly (and says so on screen) while this session may only
 *  read -- a view as a role, or an executive's account; for anything that
 *  would keep a write on this device to send later. */
export function refuseWhileViewing(storage = globalThis.localStorage) {
  const view = readOnlyNow(storage);
  if (!view) return;
  announceReadOnly(view.role);
  throw readOnlyError(view);
}

const valid = (token, now) => {
  const claims = token ? parseToken(token) : null;
  return !!claims && Number(claims.exp) * 1000 > now;
};

/**
 * Begin viewing as `role`: the server mints the token (POST /auth/view-as,
 * sent through `post`, the app's axios instance), then the admin's token and
 * workspace are set aside and the new token takes over. Answers the new
 * token; a refusal rejects with the server's error and changes nothing here.
 */
export async function startViewAs(role, { post, storage = globalThis.localStorage } = {}) {
  const res = await post('/auth/view-as', { role });
  const token = res?.data?.token;
  if (!token || !isViewAsToken(token)) throw new Error('no view-as token in the answer');
  const admin = read(storage, TOKEN_KEY);
  if (admin && !isViewAsToken(admin)) write(storage, ADMIN_TOKEN_KEY, admin);
  write(storage, ADMIN_WORKSPACE_KEY, read(storage, WORKSPACE_KEY));
  write(storage, WORKSPACE_KEY, null);
  write(storage, TOKEN_KEY, token);
  return token;
}

/**
 * Put the admin's token back, here and now, with no request: Exit must work
 * through a server mid-deploy (426) or offline. Answers the admin token, or
 * null when there is none still valid (then nobody is signed in).
 */
export function restoreAdmin({ storage = globalThis.localStorage, now = Date.now() } = {}) {
  const admin = read(storage, ADMIN_TOKEN_KEY);
  const workspace = read(storage, ADMIN_WORKSPACE_KEY);
  write(storage, ADMIN_TOKEN_KEY, null);
  write(storage, ADMIN_WORKSPACE_KEY, null);
  if (admin && !isViewAsToken(admin) && valid(admin, now)) {
    write(storage, TOKEN_KEY, admin);
    write(storage, WORKSPACE_KEY, workspace);
    return admin;
  }
  write(storage, TOKEN_KEY, null);
  write(storage, WORKSPACE_KEY, null);
  return null;
}

/**
 * Exit: the admin's token first (restoreAdmin), then the server is told with
 * the view-as token, best effort -- the session ends on its own within the
 * hour anyway, and the token can no longer be read from this device.
 */
export function exitViewAs({ storage = globalThis.localStorage, fetchFn = globalThis.fetch } = {}) {
  const viewToken = read(storage, TOKEN_KEY);
  const admin = restoreAdmin({ storage });
  if (viewToken && isViewAsToken(viewToken) && fetchFn) {
    try {
      fetchFn(apiUrl('/auth/view-as/exit'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${viewToken}`, 'X-App-Version': BUILD_ID },
      })?.catch?.(() => {});
    } catch { /* best effort */ }
  }
  return admin;
}

/** Forget both tokens and the admin's set-aside workspace (sign-in, sign-out). */
export function forgetAdmin(storage = globalThis.localStorage) {
  write(storage, ADMIN_TOKEN_KEY, null);
  write(storage, ADMIN_WORKSPACE_KEY, null);
}
